import { FC, useState } from 'react'
import Modal from '@/UI/Modals/Modal'
import { useTranslation } from 'next-i18next'

import { ModalBody, ModalFrame } from '@/UI/Modals/ModalParts'
import GrainButton from '@/shared/GrainButton'
import TextField from '@/UI/DataFields/TextField'

import { ArrowRightIcon, GridLogoIcon } from '@/components/icons'
import { hasDisplayNameContent, preventSpecialCharacters, removeSpecialCharacters } from '@/utils/stringUtils'

const STEP_KEYS = ['create_workflow', 'run_verifications', 'free_verifications'] as const

type FreeTrialModalProps = {
  // Verifications granted by the campaign, shown in the third step. Null is unlimited.
  verifications: number | null
  // Head-to-head term length; without it the unlimited title omits the day count.
  trialDays?: number | null
  isCreating?: boolean
  onCreateWorkflow: (name: string) => void
  onMaybeLater: () => void
}

/**
 * The brand panel keeps its dark green in both themes — it is artwork rather
 * than surface, and `--primary` inverts to lime in dark mode, which would leave
 * the white wordmark unreadable.
 */
const FreeTrialHero = () => (
  <div className="relative h-[215px] w-full overflow-hidden rounded-xl border-[1px] border-solid border-gray-5 bg-[#14362F]">
    <div
      className="absolute inset-0 opacity-50 [background-size:36px_36px] [background-image:linear-gradient(to_right,#4F7A71_1px,transparent_1px),linear-gradient(to_bottom,#4F7A71_1px,transparent_1px)]"
    />
    <div className="absolute inset-0 flex items-center justify-center">
      <GridLogoIcon className="relative h-[52px] w-auto [&_path]:fill-white" />
    </div>
  </div>
)

export const FreeTrialModal: FC<FreeTrialModalProps> = ({
  verifications,
  trialDays,
  isCreating = false,
  onCreateWorkflow,
  onMaybeLater,
}) => {
  const { t } = useTranslation('common')
  const [workflowName, setWorkflowName] = useState('')

  const unlimitedTitle = trialDays
    ? t('free_trial_modal.steps.free_verifications.unlimited_title', { count: trialDays })
    : t('free_trial_modal.steps.free_verifications.unlimited_title_no_term')

  return (
    <ModalFrame
      show
      onHide={onMaybeLater}
      dialogClassName="grid-medium-dialog w-[600px] max-w-[600px]"
      // `[&]:` moves the border to the variant band, so it beats the equally
      // specific `border: none` that every `.modal-content` carries.
      contentClassName="free-trial-modal-content [box-shadow:0px_4px_20px_0px_rgba(0,0,0,0.1)] [&]:[border:1px_solid_var(--gray-5)]"
    >
      <ModalBody className="flex flex-col gap-[24px] [&.modal-body]:[border-top:none]">
        <FreeTrialHero />

        <div className="flex flex-col gap-[4px]">
          <h1 className="m-0">{t('free_trial_modal.title')}</h1>
          <p className="m-0 font-switzer-main text-[16px] leading-[1.3] text-gray-3">
            {t('free_trial_modal.subtitle')}
          </p>
        </div>

        <div className="relative flex flex-col gap-[20px]">
          {/* Sits behind the opaque step circles and joins the first to the last. */}
          <span className="absolute left-[15px] top-[16px] bottom-[16px] w-px bg-gray-5" />

          {STEP_KEYS.map((key, index) => (
            <div className="relative flex items-center gap-[12px]" key={key}>
              <span className="flex size-[32px] shrink-0 items-center justify-center rounded-full bg-primary-main font-switzer-main text-[12px] font-medium leading-none text-on-primary">
                {index + 1}
              </span>
              <div className="flex min-w-0 flex-col gap-[2px]">
                <span className="font-switzer-main text-[14px] font-semibold leading-none text-gray-1">
                  {key === 'free_verifications' && verifications === null
                    ? unlimitedTitle
                    : t(`free_trial_modal.steps.${key}.title`, { count: verifications })}
                </span>
                <span className="font-switzer-main text-[12px] leading-[1.1] text-gray-3">
                  {key === 'free_verifications' && verifications === null
                    ? t('free_trial_modal.steps.free_verifications.unlimited_description')
                    : t(`free_trial_modal.steps.${key}.description`)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* A plain div, not <hr>: Bootstrap's reboot fades hr to .25 opacity and
            its `.border-0` utility is !important, so both would fight the token. */}
        <div className="h-px w-full bg-gray-5" />

        <TextField
          label={t('free_trial_modal.workflow_name_label')}
          placeholder={t('free_trial_modal.workflow_name_placeholder')}
          onKeyDown={e => preventSpecialCharacters(e)}
          onChange={e => setWorkflowName(removeSpecialCharacters(e.target.value))}
          className="p-[16px] h-[46px]"
          required
        />
      </ModalBody>

      <Modal.Footer className="flex flex-nowrap gap-[8px] [&_button]:flex-[1_1_0px]">
        <GrainButton
          type="button"
          variant="secondary"
          text={t('free_trial_modal.maybe_later')}
          onClick={onMaybeLater}
        />
        <GrainButton
          type="button"
          variant="primary"
          text={t('free_trial_modal.create_workflow')}
          icon={<ArrowRightIcon className="size-[16px]" />}
          isDisabled={!hasDisplayNameContent(workflowName)}
          isLoading={isCreating}
          onClick={() => onCreateWorkflow(workflowName)}
        />
      </Modal.Footer>
    </ModalFrame>
  )
}

export default FreeTrialModal
